import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { Link } from 'react-router';
import { getSigningApiUrl, getSigningAwsAccount } from '../amplify-config';
import { storeSignerSession } from '../auth/signer-session';
import LoadingPage from '../components/LoadingPage';
import { resolveNextOrDefault } from '../utils/safe-next';

/**
 * Passwordless magic-link login for email-only signers (#2955, Phase 3/5).
 *
 * This flow is now Cognito-free: the magic-link token is exchanged for a
 * self-issued signer JWT via `POST /api/auth/session`. The JWT is stored in
 * sessionStorage (the default non-"remember me" backend) and attached as a
 * `Bearer` token on subsequent API calls, verified in-account by the Phase-2
 * authorizer. There is NO Cognito CUSTOM_AUTH call in this path.
 *
 * Admins and SSO signers are unaffected — they never reach this page (#2848);
 * they sign in through Cognito on the standard /login form.
 *
 * Flow:
 * 1. Extract `:token` from URL params.
 * 2. POST the token to `/auth/session`.
 * 3. On 200, store the returned JWT and navigate to the `?next=` target (or
 *    `/signer`). On any non-200, show the appropriate error.
 */
function LoginByToken() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    /* #3423: nothing here is set synchronously from the effect any more. The
       missing-token case is a pure function of the URL param rather than a
       flag the effect used to raise, and `loading` derives from whether the
       exchange for THIS token has settled -- so the old
       `setMissingToken(true); setLoading(false);` pair, which was the flagged
       write, has no successor at all rather than being relocated. */
    const [settled, setSettled] = useState<{ token: string; error: string } | null>(null);

    /**
     * Post-auth navigation target derived from the `?next=` query parameter
     * (issue #1477). `resolveNextOrDefault` enforces the same-origin contract;
     * see `utils/safe-next.ts`. `decodeURIComponent` may throw on malformed
     * input — that case falls back to the safe default.
     */
    const nextTarget = useMemo(() => {
        const fallback = '/signer';
        const rawValue = searchParams.get('next');
        if (rawValue === null) return fallback;
        let decoded: string | null;
        try {
            decoded = decodeURIComponent(rawValue);
        } catch {
            decoded = null;
        }
        return resolveNextOrDefault(decoded, fallback);
    }, [searchParams]);

    const missingToken = !token;
    /* On success the component navigates away instead of settling, so the
       loader stays up until it unmounts -- exactly as before. */
    const settledError = settled !== null && settled.token === token ? settled.error : null;
    const error = settledError;
    const loading = !missingToken && settledError === null;

    useEffect(() => {
        if (!token) return;

        let cancelled = false;

        const exchange = async (): Promise<void> => {
            try {
                const response = await fetch(`${getSigningApiUrl()}/auth/session`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Aws-Account': getSigningAwsAccount(),
                    },
                    body: JSON.stringify({ token }),
                });
                if (cancelled) return;

                if (!response.ok) {
                    setSettled({
                        token,
                        error:
                            response.status === 403
                                ? 'This account cannot sign in with a magic link. Contact your administrator.'
                                : // 401 (invalid / expired / already-used) and anything
                                  // else surface the generic invalid-link message.
                                  'This sign-in link is invalid or has already been used. Please request a new one.',
                    });
                    return;
                }

                const data = (await response.json()) as { token: string };
                if (cancelled) return;
                if (!data.token) {
                    setSettled({ token, error: 'This sign-in link is invalid or has already been used. Please request a new one.' });
                    return;
                }

                // Store the self-issued signer JWT in sessionStorage and navigate.
                storeSignerSession(data.token);
                navigate(nextTarget, { replace: true });
            } catch {
                if (cancelled) return;
                setSettled({ token, error: 'Failed to load the sign-in link. Please check your connection and try again.' });
            }
        };

        void exchange();

        return () => {
            cancelled = true;
        };
    }, [token, navigate, nextTarget]);

    if (missingToken) {
        return (
            <Container
                maxWidth="sm"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Invalid Sign-In Link
                    </Typography>
                    <Typography role="alert" aria-live="assertive" color="text.secondary" sx={{ mt: 1 }}>
                        The sign-in link is missing or invalid.
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (loading) {
        return <LoadingPage message="Signing in..." />;
    }

    if (error) {
        return (
            <Container
                maxWidth="sm"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Unable to Sign In
                    </Typography>
                    <Alert severity="error" role="alert" aria-live="assertive" sx={{ mt: 1, textAlign: 'left' }}>
                        {error}
                    </Alert>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    return <LoadingPage message="Authenticating..." />;
}

export default LoginByToken;
